import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  CurrentSubscriptions as CurrentSubscriptionsView,
  CurrentSubscriptionsCopy,
} from "@bluebottlecoffee/design-system/components/Account/Subscriptions/CurrentSubscriptions";
import {
  CurrentSubscriptionCard,
  CurrentSubscriptionCardProps,
} from "@bluebottlecoffee/design-system/components/Account/Subscriptions/CurrentSubscriptionCard";
import {
  Address,
  CancellationReason,
} from "@bluebottlecoffee/design-system/components/lib/types";
import { EditFormProps } from "@bluebottlecoffee/design-system/components";
import usePlacesAutocompleteService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { noop } from "lodash";
import cookie from "js-cookie";
import {
  useSubscription,
  useSubscriptions,
  useUser,
  useAuth,
  Subscription,
  SubscriptionLineItem,
} from "@chordcommerce/react-autonomy";
import { toSubALCAddOn } from "../../../../lib/hooks/useSubALCAddOns";
import { CartQueryType } from "../../../../lib/sanity/shared";
import { handleBrightbackSession } from "../../../../lib/integrations/brightback/handle-brightback-session";
import {
  getStaticAccountPaths,
  PageData,
} from "../../../../lib/sanity/account-shared-queries";
import { AccountPage } from "../../../../components/AccountPage";
import {
  CurrentSubscriptionCardTransformerReturnProps,
  HandleEditSubscriptionType,
  toSubscriptionCardProps,
} from "../../../../lib/transformers/current-subscriptions";
import { buildLocalizedUrl } from "../../../../lib/link-builders";
import {
  getStaticAccountCurrentSubscriptionsProps,
  SanityQueryResultType,
} from "../../../../lib/sanity/current-subscriptions-queries";
import { isDefined } from "../../../../lib/utils";
import {
  formatAddressSearchString,
  handleSelectAddressPrediction,
} from "../../../../lib/component-utils/account-addresses";
import { useIsClient } from "../../../../lib/hooks/use-is-client";

export type CurrentSubsPageData = PageData<CurrentSubscriptionsCopy> &
  CartQueryType & {
    sanityQueryResult: SanityQueryResultType;
  };

export type SubsUpdatedType = {
  updated: boolean;
  latest: boolean;
  id: number;
}[];

export const getStaticProps: GetStaticProps<CurrentSubsPageData> =
  getStaticAccountCurrentSubscriptionsProps();

export const getStaticPaths: GetStaticPaths = getStaticAccountPaths;

/**
 * Compares the current and next Card's state to determine their order
 */
function byActivePausedCanceled(
  { state: xState, ...xProps }: CurrentSubscriptionCardTransformerReturnProps,
  { state: yState, ...yProps }: CurrentSubscriptionCardTransformerReturnProps,
) {
  const xCreatedAtDate = new Date(
    xProps.propsShipmentInfo.createdAtDate,
  ).getTime();
  const yCreatedAtDate = new Date(
    yProps.propsShipmentInfo.createdAtDate,
  ).getTime();

  const isPrepaidOrGift = (state: string) =>
    state === "gift" || state === "prepaid";

  const xIsPrepaidEnded =
    isPrepaidOrGift(xState) && xProps.prepaidData?.installmentRemaining === 0;
  const yIsPrepaidEnded =
    isPrepaidOrGift(yState) && yProps.prepaidData?.installmentRemaining === 0;

  const getPriority = (state: string, isPrepaidEnded: boolean) => {
    if (state === "active" || (isPrepaidOrGift(state) && !isPrepaidEnded))
      return 0; // "active and prepaid or gift active"
    if (state === "paused") return 1; // "paused"
    if (isPrepaidOrGift(state) && isPrepaidEnded) return 2; // "expired prepaid subs"
    return 3; // "canceled"
  };

  const xPriority = getPriority(xState, xIsPrepaidEnded);
  const yPriority = getPriority(yState, yIsPrepaidEnded);

  if (xPriority !== yPriority) {
    return xPriority - yPriority;
  }

  return xCreatedAtDate - yCreatedAtDate;
}

type SubscriptionCardProps = {
  cardProps: CurrentSubscriptionCardProps;
  subscriptionId: number;
};

const SubscriptionCard: FunctionComponent<SubscriptionCardProps> = ({
  cardProps,
  subscriptionId,
}) => {
  const { cancelSubscription } = useSubscription({
    subscriptionId,
  });
  const { user } = useUser();
  const [brightbackSessionUrl, setBrightbackSessionUrl] = useState<
    string | null
  >(null);

  function setCancellationReasonCookie(selectedReason: CancellationReason) {
    const fifteenMinutesFromNow = 1 / 96;
    cookie.set("subscriptionCancellationReason", selectedReason.text, {
      expires: fifteenMinutesFromNow,
    });
  }

  function handleCancelSubmit(selectedReason: CancellationReason) {
    cancelSubscription().then(() => {
      setCancellationReasonCookie(selectedReason);
    });
  }

  useEffect(() => {
    if (user.data) {
      const { email, id, name } = user.data;
      if (email && id) {
        const { propsShipmentInfo, propsSubscriptionInfo } = cardProps;
        const {
          itemDetails: { frequency, price, productName, sku },
        } = propsSubscriptionInfo;
        const value = price.amount;
        const { district: addressState } = propsShipmentInfo.shippingAddress;
        handleBrightbackSession({
          addressState,
          callback: setBrightbackSessionUrl,
          email,
          id,
          name,
          frequency,
          productName,
          sku,
          subscriptionId,
          value,
        });
      }
    }
  }, [JSON.stringify(user?.data)]);

  return (
    <CurrentSubscriptionCard
      key={`${subscriptionId}_wrapper`}
      {...{
        ...cardProps,
        propsCancelSubscription: {
          ...cardProps.propsCancelSubscription,
          cancelLink: {
            ...cardProps.propsCancelSubscription.cancelLink,
            url:
              process.env.NEXT_PUBLIC_BRIGHTBACK_ENABLED === "true" &&
              brightbackSessionUrl,
          },
          offers: {
            ...cardProps.propsCancelSubscription.offers,
            ctas: cardProps.propsCancelSubscription.offers.ctas.map((cta) =>
              cta.flow === "cancel"
                ? { ...cta, onEvent: handleCancelSubmit }
                : cta,
            ),
          },
        },
      }}
    />
  );
};

const CurrentSubscriptions: FunctionComponent<CurrentSubsPageData> = (
  props,
) => {
  const isClient = useIsClient();
  const { token } = useAuth();
  const { accountMenuCopy, pageCopy, sanityQueryResult, region, lang } = props;

  const {
    currentSubscriptionsPage: { exploreSubscriptionsLink },
    subscriptionAddOn,
  } = sanityQueryResult;

  const subscriptionAddOnProps = toSubALCAddOn({
    subscriptionAddOn,
    lang,
    region,
  });

  const { user, loadUser } = useUser();
  const {
    subscriptions: allSubscriptions,
    loadSubscriptions,
    isFetching,
  } = useSubscriptions();
  // Filter out subscriptions that are tagged "Testing"
  const subscriptions = allSubscriptions.filter(
    (sub) => !sub.tagList.find((tag) => tag === "Testing"),
  );
  const [addressQueries, setAddressQueries] = useState<
    EditFormProps["addressPredictions"]
  >([]);
  const { placesService, queryPredictions, getQueryPredictions } =
    usePlacesAutocompleteService({
      apiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY,
    });
  const [showQueryPredictions, setShowQueryPredictions] =
    useState<boolean>(false);
  const [cancelDeflectionData, setCancelDeflectionData] = useState<{
    action: CurrentSubscriptionCardProps["openModalFlow"];
    subId: number;
  }>();
  const [cardOpen, setCardOpen] = useState<number | null>(1);
  const [subsLoading, setSubsLoading] = useState<boolean>(true);
  // We cannot use isLoaded from the useSubscriptions hook because we must wait
  // for the explicit loadSubscriptions response in the useEffect below
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [currentSubscriptionCardProps, setCurrentSubscriptionCardProps] =
    useState<
      Omit<
        CurrentSubscriptionCardProps,
        "index" | "cardOpen" | "setCardOpen" | "subALCAddOn"
      >[]
    >([]);
  const [subsUpdated, setSubsUpdated] = useState<SubsUpdatedType>([]);

  function isValidOpenModalFlow(
    flow: string,
  ): flow is CurrentSubscriptionCardProps["openModalFlow"] {
    return flow === "pause" || flow === "skip" || flow === "swap";
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get("action");
    const subId = params.get("subId");
    if (action && isValidOpenModalFlow(action) && subId) {
      setCancelDeflectionData({ action, subId: Number(subId) });
    }
  }, []);

  // prevent multiple calls to useEffect with empty queryPredictions array
  // stringifying works whereas returning an array creates a new reference
  const queryPredictionsMemo = useMemo(
    () => JSON.stringify(queryPredictions),
    [queryPredictions],
  );

  useEffect(() => {
    // parse memoized prediction JSON
    const predictions = JSON.parse(queryPredictionsMemo);
    if (predictions.length) {
      setShowQueryPredictions(true);
      setAddressQueries(predictions);
    } else {
      setShowQueryPredictions(false);
    }
  }, [queryPredictionsMemo]);

  const handleAddressInput = (address: Address) => {
    getQueryPredictions({
      input: formatAddressSearchString(address),
    });
  };

  const comparableSubscriptions = JSON.stringify(subscriptions ?? []);

  useEffect(() => {
    if (token) loadUser();
  }, [token]);

  useEffect(() => {
    const getSubs = async () => {
      const allSubs = await loadSubscriptions();
      // Filter out subscriptions that are tagged "Testing"
      const filteredSubs = allSubs.filter(
        (sub) => !sub.tagList.find((tag) => tag === "Testing"),
      );
      if (allSubs) {
        setIsLoaded(true);
      }
      // check for filtered subscriptions length here only after
      // loadSubscriptions response, set loading false if 0
      if (filteredSubs.length === 0) {
        setSubsLoading(false);
      }
    };
    if (token && user?.data?.id) {
      getSubs();
    }
  }, [token, user?.data?.id]);

  // Check for transformed subscriptions if isLoaded and setSubsLoading false
  useEffect(() => {
    if (isLoaded && currentSubscriptionCardProps.length) {
      setSubsLoading(false);
    }
  }, [isLoaded, currentSubscriptionCardProps.length]);

  useEffect(() => {
    const base = process.env.NEXT_PUBLIC_CLOUDFLARE_WORKER_API_HOST || "";

    async function transformSubscriptions() {
      /** Replace current line items on subscription with new line item */
      const handleEditSubscription = async (
        {
          editSubscriptionData,
          subscriptionController,
        }: HandleEditSubscriptionType,
        sub: Subscription,
      ) => {
        const { updateSubscription } = subscriptionController;

        const replaceLineItems = (lineItem: {
          sku: string;
          quantity: number;
        }) => [
          lineItem,
          ...sub.lineItems.map((li: SubscriptionLineItem) => ({
            ...li,
            _destroy: true,
          })),
        ];

        try {
          const { interval, sku, quantity } =
            editSubscriptionData.coffeeType ?? {};

          const updateObj = {
            // Update Date
            ...(editSubscriptionData.shipmentDate && {
              actionableDate: editSubscriptionData.shipmentDate,
            }),
            // Update Interval Length
            ...(interval?.length && { intervalLength: interval?.length }),
            // Update interval Units
            ...(interval?.unit && { intervalUnits: interval?.unit }),
            // Update line Item
            ...(sku && {
              lineItemsAttributes: replaceLineItems({ quantity, sku }),
            }),
          };

          if (JSON.stringify(updateObj) === "{}") return;
          // Note: data contain subscription lineItem updated
          const data = await updateSubscription(updateObj);

          // eslint-disable-next-line consistent-return
          return data;
        } catch (error) {
          console.error(error);
        }
      };

      const cards = await Promise.all(
        subscriptions
          .filter((sub) => (sub.lineItems || []).length > 0 && sub.lineItems[0])
          .map(async (sub) => {
            const subLineItem =
              // @ts-ignore (see comment in handleAddToSub function above)
              sub.lineItems.find((i) => !i.oneTime || !i.oneTimeAdd) ?? // TODO: Chord change this data type errantly ahead of schedule. Adding both data types temporarily to unbreak our site while Chord works to fix on their end.
              sub.lineItems[0];
            const endpoint = `${base}/api/v1/products/${subLineItem.productSlug}/production-days`;

            const card = await fetch(endpoint, {
              mode: "cors",
              headers: {
                Authorization: token,
              },
            })
              .then((res) => res.json())
              .then((res: any) => {
                const subscriptionCard = toSubscriptionCardProps({
                  handleEditSubscription,
                  setSubsUpdated,
                  subsUpdated,
                  sub,
                  sanityQueryResult,
                  lang,
                  productionDays:
                    res.productionDays?.length > 0
                      ? res.productionDays
                      : [1, 2, 3, 4, 5],
                  onAddressInput: handleAddressInput,
                  // initialize empty predictions
                  addressPredictions: showQueryPredictions
                    ? addressQueries
                    : [],
                  onSelectAddressPrediction: (prediction) =>
                    handleSelectAddressPrediction(
                      prediction,
                      placesService,
                      () => setShowQueryPredictions(false),
                    ),
                  // noops are here to show these callbacks are now exposed
                  // from the design system
                  onCancelConfirmClick: noop,
                  onChangeOrderDateClick: noop,
                  onChangeOrderModalSave: noop,
                  onResumeDateClick: noop,
                  openModalFlow:
                    sub.id === cancelDeflectionData?.subId
                      ? cancelDeflectionData?.action
                      : undefined,
                });
                return subscriptionCard;
              })
              .catch((error) => {
                throw new Error(
                  `Failed to transform subscription with error: ${error.message},
        Subscription: ${subLineItem.name}, id: ${sub.id}`,
                );
              });
            return card;
          }),
      );

      const definedCards = cards.filter(isDefined);

      if (definedCards.length > 0) {
        const sortedCards = definedCards.sort(byActivePausedCanceled);
        setCurrentSubscriptionCardProps(sortedCards);
      }
    }

    if ((subscriptions?.length ?? 0) > 0) {
      transformSubscriptions();
    }
  }, [comparableSubscriptions]);

  return (
    isClient && (
      <AccountPage {...props} pageTitle={accountMenuCopy.currentSubscription}>
        <CurrentSubscriptionsView
          copy={pageCopy}
          cardOpen={cardOpen}
          currentSubscriptionCardProps={currentSubscriptionCardProps}
          subsUpdated={subsUpdated}
          currentSubscriptionCards={currentSubscriptionCardProps.map(
            (card, index) => {
              const isSubUpdated = subsUpdated.find(
                (subUpdated) => subUpdated.id === card.id,
              )?.updated;

              return (
                <SubscriptionCard
                  key={card.id}
                  subscriptionId={card.id}
                  cardProps={{
                    ...card,
                    index: index + 1,
                    setCardOpen,
                    cardOpen,
                    subscriptionUpdated: isSubUpdated,
                    subALCAddOn: () =>
                      subscriptionAddOnProps?.({
                        subscriptionId: card.id,
                      }),
                  }}
                />
              );
            },
          )}
          exploreSubscriptionsLink={{
            text: exploreSubscriptionsLink.text[lang],
            url: buildLocalizedUrl({
              region,
              lang,
              path: exploreSubscriptionsLink.internal,
            }),
          }}
          isSubsLoading={isFetching || subsLoading}
        />
      </AccountPage>
    )
  );
};

export default CurrentSubscriptions;
